import { useTranslation } from "react-i18next";

import { POUS } from "../framework/data";
import type { ReportAggregates } from "./reportTypes";
import { EntryExitBars } from "./components/EntryExitBars";
import { ChangeAnalysis } from "./components/ChangeAnalysis";
import { useLabels } from "./useLabels";

/**
 * Population view — the flagship before/after. A whole-cohort headline (overall
 * entry→exit shift + analysis of change), then every subdomain grouped by Pou,
 * each with its Theory of Change outcome and entry/exit bars. Mirrors the
 * Population view sheet of the source workbook.
 */
type Props = { agg: ReportAggregates };

export function PopulationView({ agg }: Props) {
  const { t } = useTranslation();
  const labels = useLabels();

  return (
    <div className="rpt-view">
      <section className="rpt-panel rpt-headline">
        <h2 className="rpt-panel__title">{t("reports.population.headline")}</h2>
        <p className="rpt-panel__lead">
          {t("reports.population.headlineLead")}
        </p>
        <div className="rpt-headline__grid">
          <div className="rpt-headline__bars">
            <EntryExitBars data={agg.population} />
          </div>
          <div className="rpt-headline__change">
            <ChangeAnalysis analysis={agg.population.analysis} />
          </div>
        </div>
      </section>

      {POUS.map((pou) => {
        const subs = agg.bySubdomain.filter((s) => s.pouId === pou.id);
        if (subs.length === 0) return null;
        return (
          <section key={pou.id} className="rpt-panel">
            <h2 className="rpt-panel__title rpt-panel__title--pou" lang="mi">
              {pou.nameMi}
            </h2>
            <ul className="rpt-sublist">
              {subs.map((s) => (
                <li key={s.subdomainId} className="rpt-subrow">
                  <div className="rpt-subrow__head">
                    <h3 className="rpt-subrow__name">
                      {labels.subdomain(s.subdomainId)}
                    </h3>
                    <p className="rpt-subrow__toc">
                      {labels.toc(s.subdomainId)}
                    </p>
                  </div>
                  <div className="rpt-subrow__bars">
                    {s.assessedEntry === 0 && s.assessedExit === 0 ? (
                      <p className="rpt-subrow__empty">
                        {t("reports.notAssessedYet")}
                      </p>
                    ) : (
                      <EntryExitBars data={s} compact />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default PopulationView;
